import React, { useCallback, useState } from "react";
import theme from "../../data/theme";

/**
 * ClipToggle — the kill switch for the highlight backdrop, on THIS device.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS EXISTS SO THE ANSWER TO "IT LOOKS BAD ON THE TV" IS A CLICK, NOT A
 * DEPLOY. Ben is running a real auction on this screen on Aug 22. Whatever the
 * backdrop does to legibility in that room cannot be known from a mockup, so
 * the feature ships with the way out already built and visible — not behind a
 * query string, not in devtools, not in a settings modal three clicks deep.
 *
 * 🔴 ON BY DEFAULT. An unset value means on: a kill switch that starts off is
 * a feature nobody sees. The stored value is only ever written when somebody
 * has made a choice, and only "0" turns it off, so a junk entry fails ON —
 * toward the shipped behaviour of the surrounding card either way.
 *
 * PER DEVICE, NEVER IN BOARD STATE — the same rule BoardScale.jsx is built on,
 * and for the same reason. His laptop, the cast TV and eleven phones are
 * different screens with different answers, and a display preference has no
 * business being published to the room.
 * ══════════════════════════════════════════════════════════════════════
 */
export const CLIPS_KEY = "mock1_board_clips_v1";

/**
 * Whether clips are on. Wrapped because localStorage throws outright in some
 * locked-down browsers, and in that case the answer is the default rather than
 * a broken board.
 */
export function readClipsEnabled() {
  try {
    return window.localStorage.getItem(CLIPS_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeClipsEnabled(on) {
  try {
    if (on) window.localStorage.removeItem(CLIPS_KEY);
    else window.localStorage.setItem(CLIPS_KEY, "0");
    return true;
  } catch {
    return false;                     // applies for this session, does not persist
  }
}

/**
 * Read once at mount, then component state. A failed WRITE still turns the
 * backdrop off for this session — killing it matters more than remembering
 * that it was killed, and the alternative is a switch that appears to do
 * nothing at the exact moment somebody is reaching for it.
 */
export function useClipsEnabled() {
  const [on, setOn] = useState(() => readClipsEnabled());
  const choose = useCallback((next) => {
    setOn(next);
    writeClipsEnabled(next);
  }, []);
  return [on, choose];
}

/**
 * ── THE SPECTATOR'S SWITCH ──────────────────────────────────────────
 *
 * 🔴 A DIFFERENT KEY FROM THE OPERATOR'S, because they are different devices
 * answering different questions. One key would mean Ben killing clips on his
 * laptop changed what eleven phones do, which is not what either control is for.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 IT DEFAULTS ON NOW, AND THAT REVERSES A DECISION THIS FILE USED TO ARGUE
 * FOR. The old rule was "defaults off and fails closed", on the grounds that
 * "168 nominations across eleven phones on cellular is bandwidth nobody has
 * measured".
 *
 * SO IT WAS MEASURED (Aug 12 2026, HEAD against all 46 clips):
 *
 *     median 871 KB · mean 879 KB · p90 1.25 MB · max 3.18 MB (Saquon)
 *     all 46 together .................................. 39.5 MB
 *     Giphy serves cache-control: max-age=604800
 *
 * ONE FETCH PER CLIP, NOT PER NOMINATION. The card loops from the buffer, the
 * full-bleed beat re-uses the same URL, and a week of cache means a reload or a
 * re-nomination costs nothing. So the per-phone worst case for a whole draft is
 * ~40 MB — every clip-having player nominated, once each — not 168 × anything.
 *
 * AND THE FEED ITSELF ALREADY COSTS 20 MB. /watch polls a growing board every
 * 4s: 445 B empty, 15.4 KB at 160 sales, ~20.3 MB uncompressed over three hours
 * (~5 MB gzipped), unconditionally, on every phone. A page that already assumes
 * that much cannot claim 40 MB of one-time video is the thing that breaks it.
 *
 * The real rule the old ban was a blunt form of still holds — a phone that
 * cannot afford this must pull nothing — so it is now expressed precisely,
 * below, instead of by denying it to everyone.
 *
 * 🔴 THE ONE ARGUMENT THAT DECIDED IT: if eleven people have to find a toggle,
 * the feature does not exist for the room. An opt-in on a page opened once,
 * mid-draft, from a text message, is off forever in practice.
 * ══════════════════════════════════════════════════════════════════════
 */
export const WATCH_CLIPS_KEY = "mock1_watch_clips_v1";

// effectiveType below 4g. These are the buckets where a 3 MB burst is felt.
const SLOW_TYPES = new Set(["slow-2g", "2g", "3g"]);

/**
 * What to do for a viewer who has not chosen — the ONLY place the default is
 * decided.
 *
 * 🔴 `saveData` IS AN EXPLICIT REQUEST AND IT WINS. It is the user telling
 * their own OS to spare their data; a spectator page is not the surface to
 * second-guess that on.
 *
 * ⚠️ ABSENT `navigator.connection` MEANS YES, NOT NO. Safari and Firefox do not
 * implement it at all, so treating "no signal" as "assume constrained" would
 * turn the feature off for most iPhones in the room — which is the old
 * off-by-default with extra steps. The measured cost is what licenses that: a
 * one-time ~40 MB against a feed that already spends 20 MB regardless.
 */
export function watchClipsAuto() {
  try {
    const c = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
    if (!c) return true;
    if (c.saveData) return false;
    return !SLOW_TYPES.has(c.effectiveType);
  } catch {
    return true;
  }
}

/**
 * 🔴 THREE STATES, AND A TOUCHED TOGGLE ALWAYS WINS. "1" and "0" are choices a
 * person made and are obeyed forever; absent is "nobody has said", which is the
 * only case `watchClipsAuto` gets to answer.
 *
 * ⚠️ OFF NOW WRITES "0" RATHER THAN REMOVING THE KEY. Under the old
 * off-by-default, removing it was the same as off; under auto it would mean
 * "ask again", so a spectator who turned clips off would have them come back on
 * the next reload — the worst possible behaviour for a control whose whole
 * point is that they can stop it.
 *
 * MIGRATION: only "1" and absent were ever written, so an existing opted-in
 * viewer stays on and everybody else moves to auto. Nothing to clean up.
 */
export function readWatchClipsEnabled() {
  try {
    const v = window.localStorage.getItem(WATCH_CLIPS_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
    return watchClipsAuto();
  } catch {
    // Storage denied — we cannot read a choice, so this is the no-choice case.
    return watchClipsAuto();
  }
}

export function writeWatchClipsEnabled(on) {
  try {
    window.localStorage.setItem(WATCH_CLIPS_KEY, on ? "1" : "0");
    return true;
  } catch {
    return false;
  }
}

export function useWatchClipsEnabled() {
  const [on, setOn] = useState(() => readWatchClipsEnabled());
  const choose = useCallback((next) => {
    setOn(next);
    writeWatchClipsEnabled(next);
  }, []);
  return [on, choose];
}

export default function ClipToggle({ on, onChange }) {
  return (
    <button
      data-testid="clip-toggle"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      title={on ? "Turn the highlight backdrop off for this device" : "Turn the highlight backdrop on for this device"}
      style={{
        background: on ? theme.colors.brand.orange : "transparent",
        border: `1px solid ${on ? theme.colors.brand.orange : theme.colors.border.default}`,
        borderRadius: theme.radius.sm,
        color: on ? "#fff" : theme.colors.text.muted,
        fontFamily: "inherit", fontSize: theme.fontSize.micro,
        padding: "2px 9px", cursor: "pointer",
        textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: theme.fontWeight.bold,
      }}
    >Clips {on ? "on" : "off"}</button>
  );
}
